import React, { useState } from "react";
import { APPS_SCRIPT_URL } from "../config/constants";

const GetInTouch = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitted(false);

    try {
      const response = await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData),
      });

      const data = await response.json();

      if (data.result === "success") {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="border-2 border-dotted border-gray-400 rounded-lg p-4 sm:p-6 md:p-8 m-2 sm:m-4 md:m-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Side: Form */}
            <div className="w-full lg:w-1/2">
              <p className="text-sm text-gray-600 mb-1">Contact</p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-indigo-700">
                Get In Touch
              </h1>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                Whether you have questions, feedbacks, want to get involved, or simply want
                to learn more, we're here to help. Fill out the form below and
                one of our team members will get back to you as soon as
                possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name"
                    className="w-full p-2 border border-gray-300 rounded text-sm sm:text-base"
                    required
                    aria-label="Name"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    className="w-full p-2 border border-gray-300 rounded text-sm sm:text-base"
                    required
                    aria-label="Email"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded text-sm sm:text-base"
                    required
                    aria-label="Message"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition duration-300 text-sm sm:text-base"
                >
                  Submit
                </button>
              </form>

              {isSubmitted && (
                <div className="mt-4 text-green-700 text-sm sm:text-base">
                  Thank you! Your message has been sent successfully.
                </div>
              )}
              {error && <div className="mt-4 text-red-600 text-sm sm:text-base">{error}</div>}

              <div className="mt-6">
                <h2 className="text-lg sm:text-xl font-semibold mb-2">Connect with us</h2>
                <div className="flex space-x-2">
                  {/* Social media icons (unchanged) */}
                  {/* Facebook */}
                  <a
                    target="_blank"
                    href="https://www.facebook.com/gambianstudentsinindia/"
                    className="bg-gray-100 bg-opacity-50 p-2 border border-gray-300 rounded-md transition duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                    </svg>
                  </a>
                  {/* Instagram */}
                  <a
                    target="_blank"
                    href="https://www.instagram.com/gam_ind_association/"
                    className="bg-gray-100 bg-opacity-50 p-2 border border-gray-300 rounded-md transition duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  {/* Twitter */}
                  <a
                    target="_blank"
                    href="https://x.com/Gam_Ind_Assoc"
                    className="bg-gray-100 bg-opacity-50 p-2 border border-gray-300 rounded-md transition duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                  {/* Linkedin */}
                  <a
                    target="_blank"
                    href="https://www.linkedin.com/company/association-of-gambian-students-in-india/"
                    className="bg-gray-100 bg-opacity-50 p-2 border border-gray-300 rounded-md transition duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Side: Google Maps */}
            <div className="w-full lg:w-1/2 mt-8 lg:mt-0">
              <div className="h-[300px] sm:h-[400px] lg:h-full rounded-lg overflow-hidden border-4 border-gray-300">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.303644778559!2d77.15580537508886!3d28.560643375704057!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1da8aab0b645%3A0xe0867822a8555514!2sThe%20Gambia%20High%20Commission!5e0!3m2!1sen!2sin!4v1725560074772!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Gambian High Commission Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetInTouch;